import Collection from 'ol/collection'
import DrawInteraction from 'ol/interaction/draw'
import ModifyInteraction from 'ol/interaction/modify'
import SnapInteraction from 'ol/interaction/snap'
import TranslateInteraction from 'ol/interaction/translate'
import PointGeom from 'ol/geom/point'
import LineStringGeom from 'ol/geom/linestring'
import PolygonGeom from 'ol/geom/polygon'
import Feature from 'ol/feature'
import Observable from 'ol/observable'
import eventsCondition from 'ol/events/condition'
import Velocity from 'velocity-animate'
import 'velocity-animate/velocity.ui'
import 'Data/EventCollection'
import 'UI/Template'
import 'UI/Element'
import 'System/CommandsStack'
import './Base'
import '../../Layer/Vector'

/** @namespace GIS.Map.Control.OL3 */
NS("GIS.Map.Control.OL3");

/**
 * @enum {string}
 */
const controlAction = {
    drawPoint: "draw-point",
    drawLineString: "draw-line",
    drawPolygon: "draw-polygon",
    drawSquare: "draw-square",
    drawCircle: "draw-circle",
    drawRectangle: "draw-rectangle",
    drawTriangle: "draw-triangle",
    drawByCoordinates: 'draw-by-coordinates',
    modify: "modify",
    copy: 'copy',
    remove: "remove",
    forcedRemove: "forcedRemove",
    cancel: "cancel",
    save: "save",
    undo: "undo",
    redo: "redo"
};
/**
 * @enum {string}
 */
const coordinatesFormat = {
    dd: 'dd',
    dms: 'dms'
};
const WGS_84 = 'EPSG:4326';
const ANIM_DURATION = 350;
const ANIM_FN = 'ease-out';

//         todo реализовать контрол для объединения геометрий(автоматический перевод геометрии в мульти геометрию), добавление сегментов
//         todo контрол для исключения области, дырки
//         todo rotate, scale, drag control (implement), irregular polygons control!
/**
 * @class
 * @extends GIS.Map.Control.OL3.Base
 */
GIS.Map.Control.OL3.Draw = Class(GIS.Map.Control.OL3.Base, /** @lends GIS.Map.Control.OL3.Draw.prototype */ {
    /**
     * @type {Data.EventCollection.<ol.Feature>}
     * @protected
     */
    drawn: null,
    /**
     * @type {Data.EventCollection.<ol.Feature>}
     * @protected
     */
    modified: null,
    /**
     * @type {Data.EventCollection.<ol.Feature>}
     * @protected
     */
    removed: null,
    _currentAction: undefined,
    /**
     * @type {string|null}
     * @protected
     */
    get action() {
        return this._currentAction
    },
    set action(action) {
        this.runAction(action);
    },
    /**
     * @type {$.<HTMLElement>}
     */
    $control: null,
    /**
     * @type {System.CommandsStack}
     * @protected
     */
    commandsStack: null,
    /**
     * @type {GIS.Map.Layer.Vector}
     * @protected
     */
    drawOverlay: null,
    // options
    template: "GIS/Control/Draw/Panel",
    drawControlsTemplate: "GIS/Control/Draw/DrawControls",
    coordinatesFormTemplate: "GIS/Control/Draw/CoordinatesForm",
    coordinatesRowTemplate: "GIS/Control/Draw/CoordinatesRow",
    drawControls: [],
    actionControls: [],
    coordinatesGeometry: 'point',
    coordinatesFormat: coordinatesFormat.dd,
    coordinatesProjection: DISPLAY_PROJ,
    /** @type {ol.Collection} */
    features_: null,
    /**
     * Array of layers or layers names (as added on map) which are available for modification.
     * Defaults is empty array, i. e. modification available only for new features
     * @type {string[]|Array.<GIS.Map.Layer.Base>}
     */
    layers: [],
    nonCopyFields: ['id', 'uid', 'uuid', 'guid', 'feature'],
    // private
    _points: undefined,
    _$coordinatesForm: undefined,
    _featureFromCoordinates: undefined,
    /**
     * @param {GIS.UI.Map} map
     * @param {Object} [options]
     */
    initialize(map, options) {
        options = _.defaults(options || {}, this.Class.defaults());
        this.Parent(map, options);

        this.drawControls = _.pickBy(this.Class.drawControls, control => _.includes(options.drawControls, control.action));
        this.actionControls = _.pickBy(this.Class.actionControls, control => _.includes(options.actionControls, control.action));

        this.commandsStack = new System.CommandsStack();

        this.drawn = new Data.EventCollection();
        this.modified = new Data.EventCollection();
        this.removed = new Data.EventCollection();

        this.features_ = new Collection();

        this.addOverlays();
        this.render();

        this.map_.on("feature.select", this.onMapFeatureSelect, this)
            .on("feature.unselect", this.onMapFeatureUnSelect, this)
            .on("key.down", this.onKeyDown, this);
    },
    /**
     * @protected
     */
    addOverlays() {
        this.drawOverlay = new GIS.Map.Layer.Vector(this.map_, {
            // overlay: true,  // todo interaction translate not work over features in overlay
        });

        this.layers.push(this.drawOverlay);

        this.drawOverlay.on("destroy", () => {
            _.remove(this.layers, this.drawOverlay);
            this.drawOverlay = null;
        });
    },
    /**
     * @returns {GIS.Map.Layer.Vector}
     */
    getDrawOverlay() {
        return this.drawOverlay;
    },
    /**
     * @protected
     */
    removeOverlays() {
        this.drawOverlay.destroy();
    },
    /**
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    destroy() {
        this.reset();
        this.removeOverlays();

        this.map_.off("feature.select", this.onMapFeatureSelect, this)
            .off("feature.unselect", this.onMapFeatureUnSelect, this)
            .off("key.down", this.onKeyDown, this);

        this.drawControls = [];
        this.actionControls = [];
        this.layers = [];

        this.drawn = null;
        this.modified = null;
        this.removed = null;

        this.features_.clear() && (this.features_ = null);

        Velocity(
            this.$control[0], {
                opacity: 0,
                scaleX: 1.4,
                scaleY: 1.2
            },
            ANIM_DURATION,
            ANIM_FN
        ).then(() => this.$control.remove() && (this.$control = null));

        return this.ParentCall();
    },
    /**
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    render() {
        this.$control = UI.Template.$render(this.template, {
                control: this
            })
            .appendTo(this.target || this.map.element);

        UI.Element.initEvent("click", "on-click", this.$control, this);
        UI.Element.initEvent("change", "on-change", this.$control, this);

        if (this.map_.getInteraction("select").getFeatures().getLength()) {
            enable(this.getControl(controlAction.remove));
        }

        // todo move animation code to decorators or calling code
        // run render animation
        var $controls = this.$control.find('.control').css('transform', 'scaleX(0) scaleY(0)'),
            createAnimation = control => {
                return {
                    e: control,
                    p: {
                        scaleX: [1, 0],
                        scaleY: [1, 0]
                    },
                    o: {
                        duration: ANIM_DURATION * 0.8,
                        easing: ANIM_FN,
                        delay: ANIM_DURATION * 0.25,
                        sequenceQueue: false
                    }
                };
            },
            animateControls = () => {
                Velocity.RunSequence(
                    _.chain($controls).slice(0, _.parseInt($controls.length / 2))
                    .reverse()
                    .map(createAnimation)
                    .value()
                );
                Velocity.RunSequence(
                    _.chain($controls).slice(_.parseInt($controls.length / 2))
                    .map(createAnimation)
                    .value()
                );
            };

        Velocity(this.$control, {
            opacity: [1, 0],
            scaleX: [1, 0]
        }, {
            duration: ANIM_DURATION,
            easing: ANIM_FN,
            begin: () => _.delay(animateControls, ANIM_DURATION * 0.2)
        });

        return this;
    },
    /**
     * @param {string[]} drawControls
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    setDrawControls(drawControls) {
        this.drawEnd();

        this.drawControls = _.pickBy(this.Class.drawControls, control => _.includes(drawControls, control.action));

        UI.Template.$render(this.drawControlsTemplate, {
                control: this
            })
            .appendTo(this.$control.find('.draw-controls').empty());

        return this;
    },
    /**
     * @returns {Data.EventCollection.<ol.Feature>}
     */
    getDrawn() {
        return this.drawn;
    },
    /**
     * @returns {Data.EventCollection.<ol.Feature>}
     */
    getRemoved() {
        return this.removed;
    },
    /**
     * @returns {Data.EventCollection.<ol.Feature>}
     */
    getModified() {
        return this.modified;
    },
    /**
     * @param {Array.<GIS.Map.Layer.Base>} layers
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    setLayers(layers) {
        this.layers = layers;

        return this;
    },
    /**
     * Adds feature to `drawn` Collection and to `draw` overlay.
     *
     * @fires GIS.Map.Control.OL3.Draw#feature.add
     * @param {ol.Feature} feature
     * @returns {ol.Feature}
     */
    addFeature(feature) {
        if (!this.drawn.contains(feature)) {
            this.drawn.push(feature);
        }

        this.map_.setFeatureStyleName(feature, 'modify', 'modify')

        if (!_.includes(this.drawOverlay.getFeatures(), feature)) {
            this.drawOverlay.insert(feature);
        }

        /**
         * @event GIS.Map.Control.OL3.Draw#feature.add
         * @type {{feature: ol.Feature}}
         */
        this.emit("feature.add", {
            feature
        });

        return feature;
    },
    /**
     * Removes feature from `drawn`, `modified` collections and from overlays.
     *
     * @fires GIS.Map.Control.OL3.Draw#feature.remove
     * @param {ol.Feature} feature
     * @returns {ol.Feature}
     */
    removeFeature(feature) {
        if (this.drawn.contains(feature)) {
            feature.drawn_ = true;
            this.drawn.remove(feature);
        }

        if (this.modified.contains(feature)) {
            feature.modified_ = true;
            this.modified.remove(feature);
        }

        this.removed.push(feature);
        this.drawOverlay.remove(feature);
        this.map_.unselectFeature(feature);
        this.map_.setFeatureStyleName(feature, 'remove', 'remove')

        /**
         * @event GIS.Map.Control.OL3.Draw#feature.remove
         * @type {{feature: ol.Feature}}
         */
        this.emit("feature.remove", {
            feature
        });

        return feature;
    },
    /**
     * Adds feature to `modified` collection if it is not added to `drawn`, `modified` collections and draw it on `draw` overlay.
     * If `geometry` passed, it will be the new feature's geometry.
     * If `initial` passed (initial feature state) then remove feature from `modified` collection and from overlay
     *
     * @fires GIS.Map.Control.OL3.Draw#feature.modify
     * @param {ol.Feature} feature
     * @param {Object} [prevFeature]
     * @param {boolean} [initial=false]
     * @returns {ol.Feature}
     */
    modifyFeature(feature, prevFeature, initial) {
        if (prevFeature) {
            feature.getGeometry().setCoordinates(prevFeature.geometry.coordinates);
            feature.setProperties(prevFeature.properties);
        }
		feature.values_.geog = this.map.getMap().geomToGeoJSON(feature.getGeometry(), this.map.getProjection(), this.map.displayProjection);
        if (initial) {
            this.map_.unselectFeature(feature);
            this.map_.setFeatureStyleName(
                feature,
                _.get(prevFeature, 'properties.prevStyleName') || 'default',
                _.get(prevFeature, 'properties.prevStyleName') || 'default'
            )
            this.drawOverlay.remove(feature);
            this.modified.remove(feature);
        } else {
            if (!this.drawn.contains(feature) && !this.modified.contains(feature)) {
                this.modified.push(feature);
            }

            this.map_.setFeatureStyleName(feature, 'modify', 'modify')
            /**
             * @event GIS.Map.Control.OL3.Draw#feature.modify
             * @type {{feature: ol.Feature}}
             */
            this.emit("feature.modify", {
                feature
            });
        }

        return feature;
    },
    /**
     * Rollbacks feature's state.
     * If feature exists in `drawn` collection then modify it's geometry to the new state.
     * If feature exists in `modified` collection then modify it's geometry to the new state.
     * If feature exists in `removed` collection then restore it in previous collection and redraw.
     *
     * @param {ol.Feature} feature
     * @param {Object} [prevFeature]
     * @param {boolean} [initial=false]
     * @returns {ol.Feature}
     */
    rollbackFeature(feature, prevFeature, initial) {
        var handled = false;

        this.map_.unselectFeature(feature);

        switch (true) {
            case this.drawn.contains(feature):
                if (prevFeature) {
                    this.modifyFeature(feature, prevFeature);
                } else {
                    this.removeFeature(feature);
                }
                break;
            case this.modified.contains(feature):
                this.modifyFeature(feature, prevFeature, initial);
                break;
            case this.removed.contains(feature):
                this.removed.remove(feature);

                if (feature.drawn_) {
                    delete feature.drawn_;
                    this.addFeature(feature);
                }

                if (feature.modified_) {
                    delete feature.modified_;
                    this.modifyFeature(feature);
                }
                break;
        }

        return feature;
    },
    /**
     * Saves feature state, i.e. removes it from all collections and `draw` overlay
     *
     * @fires GIS.Map.Control.OL3.Draw#feature.save Fires if feature saved
     * @param {ol.Feature} feature
     * @returns {ol.Feature}
     */
    saveFeature(feature) {
        var handled;

        this.drawOverlay.remove(feature);
        this.map_.unselectFeature(feature);
        this.map_.setFeatureStyleName(feature, 'default', 'default')

        handled = this.drawn.remove(feature) ||
            this.modified.remove(feature) ||
            // todo loop through this.layers and remove feature from them ?
            this.removed.remove(feature);

        if (handled) {
            this.map_.setFeatureStyleName(feature, 'default', 'default')
            // remove all commands of this feature
            this.commandsStack.slice().forEach(function(cmd, i) {
                if (cmd.options.feature === feature) {
                    this.commandsStack.remove(cmd);
                }
            }, this);

            if (!this.commandsStack.length) {
                disable(this.getControl("undo"));
            }

            /**
             * @event GIS.Map.Control.OL3.Draw#feature.save
             * @type {{feature: ol.Feature}}
             */
            this.emit("feature.save", {
                feature
            });
        }

        return feature;
    },
    /**
     * @param {string} action
     * @returns {Element}
     */
    getControl(action) {
        return this.$control[0].querySelector(`[data-action="${action}"]`);
    },
    /**
     * @param {function(elem: Element, key: number)} predicate
     * @param {*} [context]
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    eachControl(predicate, context = this) {
        _.each(this.$control[0].querySelectorAll(".control"), predicate, context);

        return this;
    },
    /**
     * @param {function(elem: Element, key: number)} predicate
     * @param {*} [context]
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    eachActionControl(predicate, context = this) {
        _.each(this.$control[0].querySelectorAll(".action-controls .control"), predicate, context);

        return this;
    },
    /**
     * @param {function(elem: Element, key: number)} predicate
     * @param {*} [context]
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    eachDrawControl(predicate, context = this) {
        _.each(this.$control[0].querySelectorAll(".draw-controls .control"), predicate, context);

        return this;
    },
    /**
     * @param {string} action
     * @throws RuntimeError
     */
    runAction(action) {
        if (action == null || this._currentAction === action) {
            this.drawEnd();
            return;
        }

        this._currentAction = action;

        switch (this._currentAction) {
            case controlAction.drawPoint:
            case controlAction.drawLineString:
            case controlAction.drawPolygon:
            case controlAction.drawSquare:
            case controlAction.drawCircle:
            case controlAction.drawRectangle:
            case controlAction.drawTriangle:
                this.draw(actionToGeometryName(this._currentAction));
                break;
            case controlAction.drawByCoordinates:
                this.drawByCoordinates();
                break;
            case controlAction.modify:
                this.modify();
                break;
            case controlAction.copy:
                this.copy();
                break;
            case controlAction.remove:
                this.remove();
                break;
            case controlAction.forcedRemove:
                this.remove(true);
                break;
            case controlAction.cancel:
                this.cancel();
                break;
            case controlAction.save:
                this.save();
                break;
            case controlAction.undo:
                this.undo();
                break;
            case controlAction.redo:
                this.redo();
                break;
            default:
                throw new RuntimeError('unknown action');
                break;
        }

        var notSwitchable = [
            controlAction.remove,
            controlAction.forcedRemove,
            controlAction.cancel,
            controlAction.save,
            controlAction.undo,
            controlAction.redo,
            controlAction.copy
        ];

        if (_.includes(notSwitchable, this._currentAction)) {
            this._currentAction = null;
        }
    },
    /**
     * @param {HTMLElement} el
     * @param {Event} e
     * @protected
     */
    onControlClick(el, e) {
        e.preventDefault();

        if (el.hasAttribute("disabled")) {
            return;
        }

        this.action = el.dataset.action;
    },
    /**
     * Runs draw action.
     *
     * @param {string} geometryType
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    draw(geometryType) {
        var geomFunc, maxPoints, minPoints, type;

        this.drawEnd(false, true);
        activate(this.getControl(this._currentAction));

        type = geometryType;

        switch (geometryType) {
            case "Circle":
                geomFunc = DrawInteraction.createRegularPolygon(64);
                break;
            case "Square":
                type = "Circle";
                geomFunc = DrawInteraction.createRegularPolygon(4);
                break;
            case "Triangle":
                type = "Circle";
                geomFunc = DrawInteraction.createRegularPolygon(3);
                break;
            case "Rectangle":
                type = "LineString";
                maxPoints = 2;
                geomFunc = (
                    /**
                     * @param {number[]} coordinates
                     * @param {ol.geom.SimpleGeometry} [geometry]
                     */
                    (coordinates, geometry) => {
                        const start = coordinates[0];
                        const end = coordinates[1];

                        coordinates = [
                            [start, [start[0], end[1]], end, [end[0], start[1]], start]
                        ];

                        if (geometry) {
                            geometry.setCoordinates(coordinates);
                        } else {
                            geometry = new PolygonGeom(coordinates);
                        }

                        return geometry;
                    });
                break;
            case "Line":
                type = 'LineString';
                minPoints = 2;
                break;
            case "Polygon":
                minPoints = 3;
                break;
        }

        const draw = new DrawInteraction({
            type,
            maxPoints,
            minPoints,
            geometryFunction: geomFunc,
                freehandCondition: (
                    /**
                     * @param {ol.MapBrowserPointerEvent} e
                     */
                    (e) => eventsCondition.shiftKeyOnly(e) &&
                           !_.includes(["Rectangle", "Triangle", "Square", "Circle"], geometryType)
                )
        });

        draw.on("drawstart", this.onDrawStart, this);
        draw.on("drawend", this.onDrawEnd, this);

        const modify = new ModifyInteraction({
            features: this.drawOverlay.getAdapter().getFeaturesCollection()
        });

        modify.on("modifystart", this.onModifyStart, this);
        modify.on("modifyend", this.onModifyEnd, this);

        this.map_.addInteraction("GIS.Map.Control.OL3.Draw:draw", draw)
            .addInteraction("GIS.Map.Control.OL3.Draw:modify", modify);

        this.map_.addInteraction("GIS.Map.Control.OL3.Draw:snap", new SnapInteraction({
            features: this.drawOverlay.getAdapter().getFeaturesCollection()
        }));

        return this;
    },
    /**
     * @fires GIS.Map.Control.OL3.Draw#draw.start
     * @param {ol.interaction.DrawEvent} e
     * @protected
     */
    onDrawStart(e) {
        const feature = e.feature;

        [controlAction.undo, controlAction.save, controlAction.cancel].forEach(_.flow(this.getControl.bind(this), enable));
        disable(this.getControl(controlAction.redo));

        /**
         * @event GIS.Map.Control.OL3.Draw#draw.start
         * @type {{feature: ol.Feature}}
         */
        this.emit("draw.start", {
            feature
        });
    },
    /**
     * @fires GIS.Map.Control.OL3.Draw#draw.end
     * @param {ol.interaction.DrawEvent} e
     * @protected
     */
    onDrawEnd(e) {
        var self = this;
        const feature = e.feature;

        this.pushFeatureCommand(feature, {
            run() {
                    self.addFeature(feature);
                    /**
                     * @event GIS.Map.Control.OL3.Draw#draw.end
                     * @type {{feature: ol.Feature}}
                     */
                    self.emit("draw.end", {
                        feature
                    });
                },
                undo() {
                    self.rollbackFeature(feature);
                }
        });
    },
    /**
     * Draw by coordinates.
     */
    drawByCoordinates() {
        this.drawEnd(false, true);
        activate(this.getControl(this._currentAction));

        this._points = [];

        this._$coordinatesForm = UI.Template.$render(this.coordinatesFormTemplate, {
            control: this,
            projections: GIS.Map.Base.definedProjections,
            coordinatesProjection: this.coordinatesProjection,
            coordinatesGeometry: this.coordinatesGeometry
        });

        this.appendPointRow();

        if (this.coordinatesProjection !== WGS_84) {
            this._$coordinatesForm.find('[name="coordinates-format-holder"]').hide();
        }

        UI.Element.initEvent("change", "on-change", this._$coordinatesForm, this);
        UI.Element.initEvent("click", "on-click", this._$coordinatesForm, this);

        this._$coordinatesForm.appendTo(this.map.$element)
            .find('[name="projection"]')
            .focus();

        Velocity(this._$coordinatesForm, {
            opacity: [1, 0],
            transformOriginX: [0, 0],
            transformOriginY: ['50%', '50%'],
            transformPerspective: [100, 100],
            rotateY: [0, 90]
        }, ANIM_DURATION, ANIM_FN);
    },
    /**
     * @param {string} geometryType
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    setCoordinatesGeometry(geometryType) {
        this.coordinatesGeometry = geometryType;

        if (this._$coordinatesForm) {
            this._$coordinatesForm.find('[name="geometry-name"]').text(geometryType);
        }

        if (this._featureFromCoordinates) {
            this.drawOverlay.remove(this._featureFromCoordinates);
            this._featureFromCoordinates = null;
        }

        this._points = [];

        return this;
    },
    /**
     * @param {string} projection
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    setCoordinatesProjection(projection) {
        var prevProjection = this.coordinatesProjection;
        this.coordinatesProjection = projection;

        if (this._$coordinatesForm) {
            this._$coordinatesForm.find('[name="projection"]').val(this.coordinatesProjection);

            if (this.coordinatesProjection === WGS_84) {
                this._$coordinatesForm.find('[name="coordinates-format-holder"]').show();
            } else {
                this.setCoordinatesFormat(coordinatesFormat.dd);
                this._$coordinatesForm.find('[name="coordinates-format-holder"]').hide();
            }

            _.each(this._$coordinatesForm.find('.point'), (pointElem) => {
                var $point = $(pointElem),
                    coordinate = this.getPointCoordinate($point);

                coordinate[0] = Util.coalesce(coordinate[0], 0);
                coordinate[1] = Util.coalesce(coordinate[1], 0);

                coordinate = this.map_.transformCoordinateProjection(coordinate, prevProjection, this.coordinatesProjection);

                _.each($point.find('input'), function(input, i) {
                    if (input.value != null && input.value != '') {
                        input.value = _.round(coordinate[i], 6);
                    }
                });
            });
        }

        return this;
    },
    /**
     * @param {string} format
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    setCoordinatesFormat(format) {
        this.coordinatesFormat = format;

        const notEmpty = (input) => input.value != null && input.value != '';

        if (this._$coordinatesForm) {
            this._$coordinatesForm.find('[name="coordinates-format"]').val(this.coordinatesFormat);

            _.each(this._$coordinatesForm.find('.point'), (pointElem) => {
                $(pointElem).find('input').each((i, input) => {
                    if (this.coordinatesFormat === coordinatesFormat.dms && this.coordinatesProjection === WGS_84) {
                        input.type = 'text';
                        if (notEmpty(input)) {
                            input.value = GIS.Geometry.degToDms(input.value).join(' ');
                        }
                    } else {
                        if (notEmpty(input)) {
                            input.value = GIS.Geometry.dmsStringToDeg(input.value);
                        }
                        input.type = 'number';
                    }
                });
            });
        }

        return this;
    },
    /**
     * @param {$.<Element>} $point
     * @returns {number[]}
     * @protected
     */
    getPointCoordinate($point) {
        var formatter;

        if (this.coordinatesProjection === WGS_84 && this.coordinatesFormat === coordinatesFormat.dms) {
            formatter = _.ary(_.flow(_.partialRight(_.get, 'value'), GIS.Geometry.dmsStringToDeg, parseFloat), 1);
        } else {
            formatter = _.ary(_.flow(_.partialRight(_.get, 'value'), parseFloat), 1);
        }

        return _.chain($point.find('input')).filter('value').map(formatter).value();
    },
    /**
     * @protected
     */
    onCoordinatesReadyClick() {
        this.drawEnd();
    },
    /**
     * @param {Element} el
     * @param {Event} e
     * @protected
     */
    onCoordinatesProjectionSelect(el, e) {
        e.stopPropagation();

        this.setCoordinatesProjection($(el).val());
    },
    /**
     * @param {Element} el
     * @param {Event} e
     * @protected
     */
    onCoordinatesFormatChange(el, e) {
        e.stopPropagation();

        this.setCoordinatesFormat($(el).val());
    },
    /**
     * @param {Element} el
     * @param {Event} e
     * @protected
     *
     * todo use command stack for each point
     * todo move to public API method
     */
    onCoordinatesCoordinateChange(el, e) {
        e.stopPropagation();

        const $el = $(el);
        const $point = $el.closest('.point');
        const pointIndex = $point.index();
        const projection = this.coordinatesProjection;
        const geometryType = this.coordinatesGeometry;
        var coordinate;

        const updateGeometry = (feature) => {
            var args, GeomCtor;

            if (geometryType) {
                switch (geometryType) {
                    case 'point':
                        args = this._points[0];
                        GeomCtor = PointGeom;
                        break;
                    case 'line':
                        args = this._points;
                        GeomCtor = LineStringGeom;
                        break;
                    case 'polygon':
                        args = [this._points];
                        GeomCtor = PolygonGeom;
                        break;
                }

                feature.setGeometry(new GeomCtor(args));
            } else {
                if (this._points.length === 1) {
                    feature.setGeometry(new PointGeom(this._points[0]));
                } else if (this._points.length === 2) {
                    feature.setGeometry(new LineStringGeom(this._points));
                } else {
                    feature.setGeometry(new PolygonGeom([this._points]));
                }
            }
        };

        if (!projection) {
            alert('Неизвестная проекция!');
            return;
        }

        coordinate = this.getPointCoordinate($point);

        if (coordinate.length === 1) {
            this._points.splice(pointIndex, 1);

            if (this._points.length) {
                updateGeometry(this._featureFromCoordinates);
            } else if (this.drawn.contains(this._featureFromCoordinates)) {
                this.removeFeature(this._featureFromCoordinates);
                this._featureFromCoordinates = null;
            }
        } else if (coordinate.length === 2) {
            if (!this._featureFromCoordinates) {
                this._featureFromCoordinates = new Feature();
            }

            let exists = !!this._points[pointIndex];
            this._points[pointIndex] = this.map_.transformCoordinateProjection(coordinate, projection, this.map_.getProjection());
            updateGeometry(this._featureFromCoordinates);
            this.map_.fit(this._featureFromCoordinates.getGeometry(), this.map_.getSize());

            switch (geometryType) {
                case 'point':
                    if (!this.drawn.contains(this._featureFromCoordinates)) {
                        this.addFeature(this._featureFromCoordinates);

                        this.emit("draw.end", {
                            feature: this._featureFromCoordinates
                        });
                    }

                    // if set up binding to Point geometry reset coordinates window
                    this._points = [];
                    this._featureFromCoordinates = null;
                    this._$coordinatesForm.find('[name="points"]').empty();
                    break;
                case 'line':
                    if (this._points.length > 1) {
                        if (!this.drawn.contains(this._featureFromCoordinates)) {
                            this.addFeature(this._featureFromCoordinates);

                            this.emit("draw.end", {
                                feature: this._featureFromCoordinates
                            });
                        }
                    }
                    break;
                case 'polygon':
                    if (this._points.length > 2) {
                        if (!this.drawn.contains(this._featureFromCoordinates)) {
                            this.addFeature(this._featureFromCoordinates);

                            this.emit("draw.end", {
                                feature: this._featureFromCoordinates
                            });
                        }
                    }
                    break;
            }

            if (!exists) {
                this.appendPointRow().find('input').each((i, input) => {
                    if (this.coordinatesFormat === coordinatesFormat.dms && this.coordinatesProjection === WGS_84) {
                        input.type = 'text';
                    }
                }).eq(0).focus();
            }
        } else if (pointIndex !== this._points.length - 1) {
            $point.remove();
        }
    },
    /**
     * @param {number[]} [coordinate=[]]
     * @returns {$.<Element>}
     */
    appendPointRow(coordinate = []) {
        const $point = UI.Template.$render(this.coordinatesRowTemplate, {
            control: this,
            point: {
                coordinate,
                index: this._points.length,
            }
        }).appendTo(this._$coordinatesForm.find('[name="points"]'));

        if (coordinate.length === 2) {
            $point.trigger('change');
        }

        return $point;
    },
    /**
     * Runs modify action.
     *
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    modify() {
        this.drawEnd(false, true);
        activate(this.getControl(this._currentAction));

        this.map_.getInteraction("select").getFeatures().forEach((feature) => {
            this.onMapFeatureSelect({ feature });
        });

        const translate = new TranslateInteraction({
            features: this.features_
        });

        translate.on("translatestart", this.onModifyStart, this);
        translate.on("translateend", this.onModifyEnd, this);

        this.map_.addInteraction("GIS.Map.Control.OL3.Draw:translate", translate);

        const modify = new ModifyInteraction({
            features: this.features_
        });

        modify.on("modifystart", this.onModifyStart, this);
        modify.on("modifyend", this.onModifyEnd, this);

        this.map_.addInteraction("GIS.Map.Control.OL3.Draw:modify", modify);

        this.map_.addInteraction("GIS.Map.Control.OL3.Draw:snap", new SnapInteraction({
            features: this.features_
        }));

        return this;
    },
    /**
     * @param {ol.Feature} feature
     * @returns {boolean}
     * @protected
     */
    canModify(feature) {
        return _.some(this.layers, (layer) => {
            if (_.isString(layer)) {
                layer = this.map_.getLayer(layer);
            }

            return !!layer.getAdapter().findFeature(feature);
        });
    },
    /**
     * @param {ol.interaction.ModifyEvent} e
     * @protected
     */
    onModifyStart(e) {
        this.startModify(e.features.getArray());
    },
    /**
     * @fires GIS.Map.Control.OL3.Draw#modify.start
     * @param {Array.<ol.Feature>} features
     */
    startModify(features) {
        [controlAction.undo, controlAction.save, controlAction.cancel].forEach(_.flow(this.getControl.bind(this), enable));
        disable(this.getControl(controlAction.redo));

        // now ol.interaction.Modify emits events with collection of features
        // then we need to mark current modifying feature
        features.forEach((feature) => {
            if (!feature.prev_) {
                feature.prev_ = this.map_.featureToGeoJSON(feature);
            }

            feature.modifyKey_ = feature.once("change", (e) => {
                // mark current modifying feature
                e.target.modifyTarget_ = true;
                delete feature.modifyKey_;
            });
        });

        /**
         * @event GIS.Map.Control.OL3.Draw#modify.start
         * @type {{features: Array.<ol.Feature>}}
         */
        this.emit("modify.start", {
            features
        });
    },
    /**
     * @fires GIS.Map.Control.OL3.Draw#modify.end
     * @param {Array.<ol.Feature>} features
     * @param {boolean} [fromEvent=false]
     */
    stopModify(features, fromEvent = false) {
        const self = this;

        features.forEach((feature) => {
            const prevFeature = feature.prev_;
            delete feature.prev_;

            if (feature.modifyTarget_) {
                delete feature.modifyTarget_;

                this.pushFeatureCommand(feature, {
                    prevFeature,
                    run() {
                            if (fromEvent) {
                                self.modifyFeature(feature);
                                this.options.currentFeature = self.map_.featureToGeoJSON(feature);
                                fromEvent = false;
                            } else {
                                self.modifyFeature(feature, this.options.currentFeature);
                            }

                            if (feature.firstModifyCmd_ == null) {
                                feature.firstModifyCmd_ = self.commandsStack.pointer + 1;
                            }

                            /**
                             * @event GIS.Map.Control.OL3.Draw#modify.end
                             * @type {{features: Array.<ol.Feature>}}
                             */
                            self.emit("modify.end", {
                                features
                            });
                        },
                        undo() {
                            const isFirstCmd = self.commandsStack.pointer === feature.firstModifyCmd_;
                            if (isFirstCmd) {
                                delete feature.firstModifyCmd_;
                            }
                            self.rollbackFeature(feature, this.options.prevFeature, isFirstCmd);
                        }
                });
            } else if (feature.modifyKey_) {
                Observable.unByKey(feature.modifyKey_);
                delete feature.modifyKey_;
            }
        });
    },
    /**
     * @param {ol.interaction.ModifyEvent} e
     * @protected
     */
    onModifyEnd(e) {
        this.stopModify(e.features.getArray(), true);
    },
    /**
     * Runs remove action.
     *
     * @fires GIS.Map.Control.OL3.Draw#remove
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    remove(forced) {
        const self = this;
        const selection = this.map_.getInteraction("select").getFeatures();
        var handled = false;

        this.drawEnd();

        if (forced || confirm("Вы уверены что хотите удалить выделенные объекты?")) {
            _.each(selection.getArray().slice(), (feature) => {
                this.pushFeatureCommand(feature, {
                    run() {
                        self.removeFeature(feature);
                        /**
                         * @event GIS.Map.Control.OL3.Draw#remove
                         * @type {{feature: ol.Feature}}
                         */
                        self.emit("remove", {
                            feature
                        });
                    },
                    undo() {
                        self.rollbackFeature(feature);
                    }
                });

                handled = true;
            });

            if (handled) {
                [controlAction.undo, controlAction.save, controlAction.cancel].forEach(_.flow(self.getControl.bind(self), enable));
                disable(self.getControl(controlAction.redo));
            }
        }

        return this;
    },
    /**
     * @param {{feature: ol.Feature}} e
     * @protected
     */
    onMapFeatureSelect(e) {
        const feature = e.feature;

        switch (this._currentAction) {
            case controlAction.modify:
                if (this.canModify(feature)) {
                    this.features_.push(feature);
                    //this.drawOverlay.insert(feature);
                }
                break;
        }

        [controlAction.remove, controlAction.forcedRemove, controlAction.copy].forEach(_.flow(this.getControl.bind(this), enable));
    },

    selectFeatureOnMap(feature){
        if(!feature)
            return;
        this.map_.selectFeature(feature);
        [controlAction.remove, controlAction.forcedRemove, controlAction.copy].forEach(_.flow(this.getControl.bind(this), enable));
    },
    /**
     * @param {{feature: ol.Feature}} e
     * @protected
     */
    onMapFeatureUnSelect(e) {
        const feature = e.feature;

        switch (this._currentAction) {
            case controlAction.modify:
                this.features_.remove(feature);
                break;
        }

        if (!this.map_.getInteraction("select").getFeatures().getLength()) {
            [controlAction.remove, controlAction.forcedRemove, controlAction.copy].forEach(_.flow(this.getControl.bind(this), disable));
        }
    },
    /**
     * Copy features action.
     *
     * @fires GIS.Map.Control.OL3.Draw#copy
     * @returns {GIS.Map.Control.OL3.Draw}
     * todo после копирования и вставки всегда селектится оригинал, поэтому не работает транслате
     */
    copy() {
        const self = this;
        const selection = this.map_.getInteraction("select").getFeatures();
        var handled = false;

        this.drawEnd();

        selection.getArray().slice().forEach((feature) => {
            const featureCopy = feature.clone();
			if (Array.isArray(this.nonCopyFields)) {
                this.nonCopyFields.forEach(function (k) {
					featureCopy.unset(k, true);
				});
			}

            selection.remove(feature);
            selection.push(featureCopy);

            this.pushFeatureCommand(featureCopy, {
                run() {
                        self.addFeature(featureCopy);
                        /**
                         * @event GIS.Map.Control.OL3.Draw#copy
                         * @type {{feature: ol.Feature}}
                         */
                        self.emit('copy', {
                            featureCopy
                        });
                    },
                    undo() {
                        self.rollbackFeature(featureCopy);
                    }
            });

            handled = true;
        });

        if (handled) {
            [controlAction.undo, controlAction.save, controlAction.cancel].forEach(_.flow(self.getControl.bind(self), enable));
            disable(self.getControl(controlAction.redo));

            this._currentAction = controlAction.modify;
            this.modify();
        }

        return this;
    },
    /**
     * Runs save action.
     * Saves current step state and clears operations history.
     *
     * @fires GIS.Map.Control.OL3.Draw#save
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    save() {
        const added = this.drawn.items();
        const modified = this.modified.items();
        const removed = this.removed.items();

        this.drawEnd();
        this.commandsStack.clear();

        this.eachActionControl(disable);

        /**
         * @event GIS.Map.Control.OL3.Draw#save
         * @type {{added: Array.<ol.Feature>, modified: Array.<ol.Feature>, removed: Array.<ol.Feature>}}
         */
        this.emit("save", {
            added,
            modified,
            removed
        });

        return this;
    },
    /**
     * Runs cancel action.
     * Step back to first operation and clears operations history.
     *
     * @fires GIS.Map.Control.OL3.Draw#cancel
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    cancel() {
        const added = this.drawn.items();
        const modified = this.modified.items();
        const removed = this.removed.items();

        this.drawEnd();
        this.commandsStack.stepTo(0).clear();

        this.eachActionControl(disable);

        /**
         * @event GIS.Map.Control.OL3.Draw#cancel
         * @type {{added: Array.<ol.Feature>, modified: Array.<ol.Feature>, removed: Array.<ol.Feature>}}
         */
        this.emit("cancel", {
            added,
            modified,
            removed
        });

        return this;
    },
    /**
     * Runs undo action.
     *
     * @fires GIS.Map.Control.OL3.Draw#undo
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    undo() {
        this.drawEnd();

        try {
            this.commandsStack.undo();
            enable(this.getControl(controlAction.redo));
            if (this.commandsStack.isStart()) {
                disable(this.getControl(controlAction.undo));
            }
        } catch (e) {
            if (e instanceof RangeError) {
                disable(this.getControl(controlAction.undo));
            } else {
                console.warn(e.stack);
            }
        }

        /**
         * @event GIS.Map.Control.OL3.Draw#undo
         */
        this.emit("undo");

        return this;
    },
    /**
     * Runs redo action.
     *
     * @fires GIS.Map.Control.OL3.Draw#redo
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    redo() {
        this.drawEnd();

        try {
            this.commandsStack.redo();
            enable(this.getControl(controlAction.undo));
            if (this.commandsStack.isEnd()) {
                disable(this.getControl(controlAction.redo));
            }
        } catch (e) {
            if (e instanceof RangeError) {
                disable(this.getControl(controlAction.redo));
            } else {
                console.warn(e.stack);
            }
        }

        /**
         * @event GIS.Map.Control.OL3.Draw#redo
         */
        this.emit("redo");

        return this;
    },
    /**
     * Resets control.
     *
     * @fires GIS.Map.Control.OL3.Draw#reset
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    reset() {
        this.action = null;

        this.eachActionControl(disable);

        this.commandsStack.stepTo(0).clear();

        this.drawn.clear();
        this.modified.clear();
        this.removed.clear();

        this.drawOverlay.clear();

        /**
         * @event GIS.Map.Control.OL3.Draw#reset
         */
        this.emit("reset");

        return this;
    },
    /**
     * Turn off all draw interactions.
     * Must be called in each action method.
     *
     * @param {boolean} [resetAction=true]
     * @param {boolean} [resetControls=true]
     * @returns {GIS.Map.Control.OL3.Draw}
     */
    drawEnd(resetAction, resetControls) {
        resetAction = Util.coalesce(resetAction, true);
        resetControls = Util.coalesce(resetControls, true);
        this.removeDrawingInteractions();

        if (resetAction) {
            this._currentAction = null;
        }

        if (resetAction || resetControls) {
            this.eachDrawControl(deactivate);
        }

        return this;
    },
    /**
     * @protected
     */
    removeDrawingInteractions() {
        const draw = this.map_.getInteraction("GIS.Map.Control.OL3.Draw:draw");
        if (draw) {
            draw.un("drawstart", this.onDrawStart, this);
            draw.un("drawend", this.onDrawEnd, this);
            this.map_.removeInteraction("GIS.Map.Control.OL3.Draw:draw");
        }

        const modify = this.map_.getInteraction("GIS.Map.Control.OL3.Draw:modify");
        if (modify) {
            modify.un("modifystart", this.onModifyStart, this);
            modify.un("modifyend", this.onModifyEnd, this);
            this.map_.removeInteraction("GIS.Map.Control.OL3.Draw:modify");
        }

        const translate = this.map_.getInteraction("GIS.Map.Control.OL3.Draw:translate");
        if (translate) {
            translate.un("translatestart", this.onModifyStart, this);
            translate.un("translateend", this.onModifyEnd, this);
            this.map_.removeInteraction("GIS.Map.Control.OL3.Draw:translate");
        }

        const snap = this.map_.getInteraction("GIS.Map.Control.OL3.Draw:snap");
        if (snap) {
            this.map_.removeInteraction("GIS.Map.Control.OL3.Draw:snap");
        }


        if (this._$coordinatesForm) {
            Velocity(this._$coordinatesForm, 'reverse').then(() => {
                this._$coordinatesForm.remove() && (this._$coordinatesForm = null);
                this._featureFromCoordinates = null;
                this._points = null;
            });
        }

        this.features_ && this.features_.clear();
    },
    /**
     * @param {KeyboardEvent} {{ctrlKey: boolean, keyCode: number}}
     * @protected
     */
    onKeyDown({ ctrlKey, keyCode }) {
        switch (true) {
            case ctrlKey && keyCode === 90: // ctrl + z
                this.undo();
                break;
            case ctrlKey && keyCode === 89: // ctrl + y
                this.redo();
                break;
            case ctrlKey && keyCode === 27: // ctrl + esc
                this.cancel();
                break;
            case ctrlKey && keyCode === 13: // ctrl + enter
                this.save();
                break;
        }
    },
    /**
     * @param {ol.Feature} feature
     * @param {System.Command|Object} cmd
     * @protected
     */
    pushFeatureCommand(feature, cmd) {
        if (Class.Is(cmd, System.Command)) {
            cmd.options.feature = feature;
        } else {
            cmd.feature = feature;
        }

        this.commandsStack.push(cmd);
    },
    /**
     * @param {string} action
     * @returns {string}
     * @public
     * @static
     */
    actionToGeometryName,
    /**
     * @param geometryName
     * @returns {string}
     * @public
     * @static
     */
    geometryNameToAction
});

GIS.Map.Control.OL3.Draw.Static( /** @lends GIS.Map.Control.OL3.Draw */ {
    /**
     * @enum
     */
    controlAction,
    /**
     * Array of available draw controls
     * @type {Object[]}
     */
    drawControls: [{
            label: "Точка",
            description: "Инструмент рисования точки",
            action: controlAction.drawPoint,
            classes: "icon-map-marker icon-20"
        }, {
            label: "Линия",
            description: "Инструмент рисования линии",
            action: controlAction.drawLineString,
            classes: "icon-angle-down icon-20"
        }, {
            label: "Полигон",
            description: "Инструмен рисования полигона",
            action: controlAction.drawPolygon,
            classes: "icon-bookmark-o icon-20"
        }, {
            label: "Квадрат",
            description: "Инструмен рисования квадрата",
            action: controlAction.drawSquare,
            classes: "icon-checkbox-unchecked icon-20"
        }, {
            label: "Круг",
            description: "Инструмент рисования круга",
            action: controlAction.drawCircle,
            classes: "icon-radio-unchecked icon-20"
        }, {
            label: "Прямоугольник",
            description: "Инструмент рисования прямоугольника",
            action: controlAction.drawRectangle,
            classes: "icon-columns icon-20"
        }, {
            label: "Треугольник",
            description: "Инструмент рисования треугольника",
            action: controlAction.drawTriangle,
            classes: "icon-play icon-20"
        }, {
            label: "Рисование по координатам",
            description: "Инструмент рисования по координатам",
            action: controlAction.drawByCoordinates,
            classes: "icon-columns icon-20"
        }, {
            label: "Изменить",
            description: "Инструмент изменения нарисованной геометрии\nИзменение: потянуть за рамку геометрии\nУдаление: щелкнуть по точке пересечения граней",
            action: controlAction.modify,
            classes: "icon-pencil icon-20"
        }, {
            label: "Копировать",
            description: "Инструмент копирования",
            action: controlAction.copy,
            classes: "icon-copy icon-20",
            disabled: true
        }, {
            label: "Удалить",
            description: "Инструмент удаления геометрии\nВыделите одну или несколько геометрий и нажмите эту кнопку",
            action: controlAction.remove,
            classes: "icon-delete icon-20",
            disabled: true
        }, {
            label: "Удалить",
            description: "Инструмент удаления геометрии\nВыделите одну или несколько геометрий и нажмите эту кнопку",
            action: controlAction.forcedRemove,
            classes: "icon-delete icon-20",
            disabled: true
        }],
        /**
         * Array of available action controls
         * @type {Object[]}
         */
        actionControls: [{
            label: "Отменить",
            description: "Отменить последнее действие (Ctrl + Z)",
            action: controlAction.undo,
            classes: "icon-undo icon-20",
            disabled: true
        }, {
            label: "Повторить",
            description: "Повторить последнее действие (Ctrl + Y)",
            action: controlAction.redo,
            classes: "icon-redo icon-20",
            disabled: true
        }, {
            label: "Отменить",
            description: "Отменяет все последние несохраненные изменения",
            action: controlAction.cancel,
            classes: "icon-close icon-20",
            disabled: true
        }, {
            label: "Сохранить",
            description: "Сохраняет все последние несохраненные изменения",
            action: controlAction.save,
            classes: "icon-check2 icon-20",
            disabled: true
        }],
        /**
         * Returns default options for control
         * @returns {{drawControls: Array, actionControls: Array}}
         */
        defaults() {
            return {
                drawControls: _.map(this.drawControls, "action"),
                actionControls: _.map(this.actionControls, "action")
            };
        },
        /**
         * @param {string} action
         * @returns {string}
         * @public
         * @static
         */
        actionToGeometryName,
        /**
         * @param geometryName
         * @returns {string}
         * @public
         * @static
         */
        geometryNameToAction
});

// Helpers
/**
 * @param {string} action
 * @returns {string}
 * @public
 * @static
 */
function actionToGeometryName(action) {
    return _.upperFirst(action.split("-")[1]);
}

/**
 * @param geometryName
 * @returns {string}
 * @public
 * @static
 */
function geometryNameToAction(geometryName) {
    return "draw-" + _.lowerFirst(geometryName);
}

/**
 * @param {Element} control
 * @return {Element}
 */
function toggleAvailability(control) {
    if (control.hasAttribute("disabled")) {
        enable(control);
    } else {
        disable(control);
    }

    return control;
}

/**
 * @param {Element} control
 * @return {Element}
 */
function enable(control) {
    if (control) {
        control.classList.remove("disabled");
        control.removeAttribute("disabled");
    }

    return control;
}

/**
 * @param {Element} control
 * @return {Element}
 */
function disable(control) {
    if (control) {
        control.classList.add("disabled");
        control.setAttribute("disabled", null);
    }

    return control;
}

/**
 * @param {Element} control
 * @return {Element}
 */
function toggleActivity(control) {
    if (control.classList.contains("active")) {
        deactivate(control);
    } else {
        activate(control);
    }

    return control;
}

/**
 * @param {Element} control
 * @return {Element}
 */
function activate(control) {
    if (control) {
        control.classList.add("active");
    }

    return control;
}

/**
 * @param {Element} control
 * @return {Element}
 */
function deactivate(control) {
    if (control) {
        control.classList.remove("active");
    }

    return control;
}
